<template>
  <div class="rsvp-report-box">
    <div class="rsvp-report">
      <div class="form-group">
        <div class="instruction">Please select a start and end date:</div>
        <div class="row expanded">
          <div class="small-6 column padding-R">
            <div class="date-input">
              <label>Start Date:</label>
              <datepicker class="datepicker" v-model="from" format="MMMM dd, yyyy" :highlighted="highlightedDates"></datepicker>
            </div>
          </div>
          <div class="small-6 column padding-L">
            <div class="date-input end-date">
              <label>End Date:</label>
              <datepicker class="datepicker" v-model="to" format="MMMM dd, yyyy" :highlighted="highlightedDates"></datepicker>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="list-box">
      <div class="rsvp-report">
        <div class="form-group">
          <div class="download-type">
            <label :class="{'pdf':true, 'has-error':formatError}">
              <input type="checkbox" name="downloadType" v-model="pdfChecked"
                @click="formatCheck" />
              PDF
            </label>
            <label :class="{'csv':true, 'has-error':formatError}">
              <input type="checkbox" name="downloadType" v-model="csvChecked"
                @click="formatCheck" />
              CSV
            </label>
          </div>
          <div class="instruction">Please select the reports to generate:</div>
          <div class="row expanded reports">
            <div :class="['small-12 column report', reportStatus.payout, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.payout" />
                <span>{{ reportLabel('payout') }}</span>
              </label>
            </div>
            <div :class="['small-12 column report', reportStatus.serviceTypeBrand, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.serviceTypeBrand" />
                <span>{{ reportLabel('serviceTypeBrand') }}</span>
              </label>
              <select :class="{'disabled':!selectedReports.serviceTypeBrand}"
                :disabled="!selectedReports.serviceTypeBrand" v-model="selectedServiceBrand">
                <option value="bookingTime">Booking Date</option>
                <option value="pickupTime">Pickup Date</option>
                <!-- <option value="completeTime">Completed Date</option>
                <option value="cancelTime">Canceled Date</option> -->
              </select>
            </div>
            <div :class="['small-12 column report', reportStatus.cancellationReport, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.cancellationReport" />
                <span>{{ reportLabel('cancellationReport') }}</span>
              </label>
              <select :class="{'disabled':!selectedReports.cancellationReport}"
                :disabled="!selectedReports.cancellationReport" v-model="selectedCancellationType">
                <option value="all">All</option>
                <option value="consumer">Canceled by Consumer</option>
                <option value="admin">Canceled by Any Admin</option>
                <option value="_admin">Canceled by Specific Admin</option>
                <option value="goRes">Go Reservations</option>
              </select>
              <div class="admin-box" v-show="selectedCancellationType === '_admin'">
                <input type="text" v-model="searchAdmin" @focus="adminSearchFocus" @blur="adminSearchBlur"
                  :disabled="!selectedReports.cancellationReport"/>
                <ul v-show="showSearchAdminList">
                  <li v-for="admin in admins" :key="admin.id" @click="selectAdmin(admin)">{{ admin.name }}</li>
                </ul>
              </div>
            </div>
            <div :class="['small-12 column report', reportStatus.driverPaymentBonus, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.driverPaymentBonus" />
                <span>{{ reportLabel('driverPaymentBonus') }}</span>
              </label>
            </div>
            <div :class="['small-12 column report', reportStatus.insurance, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.insurance" />
                <span>{{ reportLabel('insurance') }}</span>
              </label>
            </div>
            <div :class="['small-12 column report', reportStatus.insuranceMonthly, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.insuranceMonthly" />
                <span>{{ reportLabel('insuranceMonthly') }}</span>
              </label>
              <input type="text" class="insurance-email" placeholder="Email Recipient"
              v-model="insuranceEmail" :disabled="!selectedReports.insuranceMonthly" />
              <input type="text" class="insurance-email" placeholder="Miles per day" v-on:keypress="isNumber"
              v-model="miles" :disabled="!selectedReports.insuranceMonthly" />
            </div>
            <div :class="['small-12 column report', reportStatus.portalFee, {'processing':pdf.loading}]">
              <label class="checkbox">
                <input type="checkbox" v-model="selectedReports.portalFee" />
                <span>{{ reportLabel('portalFee') }}</span>
              </label>
              <span class="note">Portal fee is calculated against Base Fare. Reports show Totals including fees.</span>
              <select :class="{'disabled':!selectedReports.portalFee}"
                :disabled="!selectedReports.portalFee" v-model="selectedBrand">
                <option value="">Select Brand</option>
                <option :value="brand.id" v-for="brand in brands.list" :key="brand.id">{{ brand.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <transition name="slide-down">
          <div class="error" v-show="error.show">
            <span v-show="!!error.message">{{ error.message }}</span>
            <ul v-show="error.empty.length">
              <li>Empty Result:</li>
              <li v-for="(empty, ix) in error.empty" :key="ix">- {{ empty }}</li>
            </ul>
          </div>
        </transition>
        <a v-bind:class="{ 'button cta success small block': true, disabled: pdf.loading }"
          href="javascript:;" @click="startProcess">
          <i class="fa fa-spinner fa-spin" v-show="pdf.loading"></i>
          <span v-show="!pdf.loading">Generate and Download Report</span>
        </a>
      </div>

      <div id="pdf-view" :class="[pdf.class]">
        <div v-if="!isServiceTypeBrand" v-for="(pageData, pageIndex) in pdf.table.data"
          :class="['page', pageIndex]">
          <div class="title">{{ pdf.title }}</div>
          <div class="date-range">{{ pdf.date }}</div>
          <table>
            <thead>
              <tr>
                <th v-for="label in pdf.table.label" v-html="label"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, index) in pageData">
                <td v-for="(value, i) in data" :class="[i]">{{ value }}</td>
              </tr>

              <tr v-if="isPayout && currentPage(pageIndex) == totalPage" class="total">
                <td>Total ({{ pdf.table.total.record }})</td>
                <td class="driverCount">{{ pdf.table.total.drivers }}</td>
                <td class="rsvpCount">{{ pdf.table.total.rsvp }}</td>
                <!-- <td class="totalPax">{{ pdf.table.total.pax }}</td>
                <td class="avgPax">{{ pdf.table.total.avgPax }}</td> -->
                <td class="fare">${{ pdf.table.total.fare }}</td>
                <td class="travelInsurance">${{ pdf.table.total.travelInsurance }}</td>
                <td class="airportFee">${{ pdf.table.total.airportFee }}</td>
                <td class="totalFare">${{ pdf.table.total.fareSum }}</td>
                <td class="avgFare">{{ pdf.table.total.avgFare }}</td>
                <!-- <td class="avgFarePP">{{ pdf.table.total.avgFarePP }}</td> -->
                <!-- <td class="charged">${{ pdf.table.total.charged }}</td> -->
                <td class="payment">${{ pdf.table.total.payment }}</td>
                <td class="paymentPercent">{{ pdf.table.total.paymentPercent }}%</td>
                <td class="pickupFee">${{ pdf.table.total.pickupFee }}</td>
                <td class="gasFee">${{ pdf.table.total.gasFee }}</td>
                <td class="tips">${{ pdf.table.total.tips }}</td>
                <td class="bonus">${{ pdf.table.total.bonus }}</td>
                <td class="cancelFee">${{ pdf.table.total.cancelFee }}</td>
                <td class="driverPayment">${{ pdf.table.total.extraFee }}</td>
                <td class="driverPercent">{{ pdf.table.total.percent }}%</td>
              </tr>

              <tr v-if="isDriverPaymentBonus && currentPage(pageIndex) == totalPage" class="total">
                <td>Total ({{ pdf.table.total.record }})</td>
                <td class="brand" colspan="3"></td>
                <td class="baseFare">${{ pdf.table.total.baseFare }}</td>
                <td class="driverPayment">${{ pdf.table.total.driverPayment }}</td>
                <td class="bonus">${{ pdf.table.total.bonus }}</td>
                <td class="avgBonus">{{ pdf.table.total.avgBonus }}%</td>
                <td class="totalDriverPayment">${{ pdf.table.total.totalDriverPayment }}</td>
                <td colspan="2"></td>
              </tr>

              <tr v-if="isInsurance && currentPage(pageIndex) == totalPage" class="total">
                <td colspan="11" class="total-p">
                  Total 
                  <span>RSVP: {{ pdf.table.total.rsvp }}</span>
                  <span>P1: {{ pdf.table.total.p1 }}</span>
                  <span>P2: {{ pdf.table.total.p2 }}</span>
                  <span>P3: {{ pdf.table.total.p3 }}</span>
                  <span>Grand Total: {{pdf.table.total.overAll}}</span>
                </td>
              </tr>

              <tr v-if="isPortalFee && currentPage(pageIndex) == totalPage" class="total">
                <td colspan="2">Total ({{ pdf.table.total.rsvp }})</td>
                <td class="baseFare">${{ pdf.table.total.fare }}</td>
                <td class="portalFee">${{ pdf.table.total.portalFee }}</td>
              </tr>

            </tbody>
          </table>
          <div class="page-count">page {{ currentPage(pageIndex) }} of {{ totalPage }}</div>
        </div>

        <div v-else>
          <div v-for="(data, pageIndex) in pdf.table.serviceTypeBrandData" :class="['page', pageIndex]">
            <div class="brand-box" v-for="(dat, brand) in pdf.table.serviceTypeBrandData[pageIndex]">
              <div class="title">{{brand}} {{ pdf.title }}</div>
              <div class="date-range">{{ pdf.date }}</div>
              <table>
                <thead>
                  <tr>
                    <th v-for="label in pdf.table.label" v-html="label"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(tableData, index) in dat">
                    <td v-for="(value, i) in tableData" :class="[i]">{{ value }}</td>
                  </tr>

                  <tr class="total">
                    <td>Total</td>
                    <td class="rsvp">{{ pdf.table.total[brand].rsvp }}</td>
                    <td class="asap">{{ pdf.table.total[brand].asap }}</td>
                    <td class="web">{{ pdf.table.total[brand].web }}</td>
                    <td class="admin">{{ pdf.table.total[brand].admin }}</td>
                    <td class="app">{{ pdf.table.total[brand].app }}</td>
                    <td class="app">{{ pdf.table.total[brand].go }}</td>
                    <td class="paxSum">{{ pdf.table.total[brand].paxSum }}</td>
                    <td class="paxAvg">{{ pdf.table.total[brand].paxAvg }}</td>
                    <td class="fareSum">${{ pdf.table.total[brand].fareSum }}</td>
                    <td class="fareAvg">{{ pdf.table.total[brand].fareAvg }}</td>
                    <td class="farePP">{{ pdf.table.total[brand].farePP }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div :class="{'modal':true, 'show':insuranceModal.show}">
      <div class="bg-overlay"></div>
      <div class="modal-content">
        <i class="fas fa-check-circle fa-5x" style="color: #2db96f"></i>
        <p v-html="insuranceModal.message"></p>

        <a class="button cta block small" @click="insuranceModal.show = false">
          {{ $t('rsvpDetails.close') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
/* global CONFIG */
import moment from 'moment-timezone'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import datepicker from 'vuejs-datepicker'
import CsvExport from 'csvexport'
import featureToggle from '@/modules/api/featureToggle'

export default {
  name: 'specialize-report',
  components: { datepicker },
  data () {
    return {
      from: null,
      to: null,
      error: {
        show: false,
        message: '',
        empty: []
      },
      selectedReports: {
        payout: false,
        serviceTypeBrand: false,
        cancellationReport: false,
        driverPaymentBonus: false,
        insurance: false,
        insuranceMonthly: false,
        portalFee: false
      },
      reportStatus: {
        payout: '',
        serviceTypeBrand: '',
        cancellationReport: '',
        driverPaymentBonus: '',
        insurance: '',
        insuranceMonthly: '',
        portalFee: ''
      },
      pdf: {
        code: '',
        class: '',
        title: '',
        date: '',
        table: {
          label: [],
          data: {},
          extra: {},
          total: {}
        },
        maxRecordPerPage: 40,
        list: [],
        current: '',
        loading: false
      },
      orientation: ['payout', 'serviceTypeBrand', 'cancellationReport', 'driverPaymentBonus', 'insurance', 'insuranceMonthly'],
      pdfChecked: true,
      csvChecked: false,
      selectedServiceBrand: 'bookingTime',
      selectedCancellationType: 'all',
      formatError: false,
      csv: {
        label: [],
        data: [],
        list: [],
        total: {}
      },
      searchAdmin: '',
      searchAdminId: '',
      searchAdminDelay: '',
      showSearchAdminList: false,
      insuranceEmail: '',
      miles: '',
      insuranceModal: {
        show: false,
        message: ''
      },
      isIOS: navigator.userAgent.match(/(iPad|iPhone|iPod)/g),
      selectedBrand: ''
    }
  },
  computed: {
    show () { return this.$store.state.reports.rsvpReport.show },
    highlightedDates () {
      let self = this
      let result = { to: null, from: null }

      if (self.from !== null) { result.from = self.from }
      if (self.to !== null) { result.to = self.to }
      return result
    },
    totalPage () { return Object.keys(this.pdf.table.data).length },
    isPayout () { return this.pdf.code === 'payout' && this.totalPage },
    isServiceTypeBrand () { return this.pdf.code === 'serviceTypeBrand' && this.totalPage },
    admins () { return this.$store.state.reservationNotes.admins.filter(x => x.name.toLowerCase().includes(this.searchAdmin.toLowerCase())) },
    isDriverPaymentBonus () { return this.pdf.code === 'driverPaymentBonus' && this.totalPage },
    isInsurance() { return this.pdf.code === 'insurance' && this.totalPage },
    isPortalFee() { return this.pdf.code === 'portalFee' && this.totalPage },
    brands () { return this.$store.state.brands }
  },
  methods: {
    isValid () {
      let self = this
      self.error.show = false
      self.error.empty = []
      if (self.from === null || self.to === null) {
        self.error.show = true
        self.error.message = 'Please specify the Start and End dates.'
        return false
      }

      let from = moment(self.from)
      let to = moment(self.to)

      if (from.isAfter(to)) {
        self.error.show = true
        self.error.message = 'End date must be after Start date.'
        return false
      }

      self.formatError = false
      if (!self.pdfChecked && !self.csvChecked) {
        self.error.show = true
        self.error.message = 'Please select which file format.'
        self.formatError = true
        return false
      }

      if (!self.selectedReports.payout && !self.selectedReports.serviceTypeBrand
        && !self.selectedReports.cancellationReport && !self.selectedReports.driverPaymentBonus
        && !self.selectedReports.insurance && !self.selectedReports.insuranceMonthly
        && !self.selectedReports.portalFee
      ) {
        self.error.show = true
        self.error.message = 'Please select at least one report type.'
        return false
      }

      if (self.selectedReports.cancellationReport && self.pdfChecked) {
        self.error.show = true
        self.error.message = 'Only CSV format is allowed for Cancellation Report.'
        return false
      }

      if (self.selectedReports.cancellationReport && self.selectedCancellationType === '_admin' &&
        !self.searchAdminId
      ) {
        self.error.show = true
        self.error.message = 'Please select an admin.'
        return false
      }

      if (self.selectedReports.insurance && to.diff(from, 'days') > 1) {
        self.error.show = true
        self.error.message = 'Max 1 day for Insurance Report (Daily)'
        return false
      }

      if (self.selectedReports.insuranceMonthly && self.pdfChecked) {
        self.error.show = true
        self.error.message = 'Only CSV format is allowed for Insurance Report (Monthly).'
        return false
      }
      if (self.selectedReports.insuranceMonthly && !self.insuranceEmail) {
        self.error.show = true
        self.error.message = 'Please enter an email.'
        return false
      }

      return true;
    },
    getNewDoc () {
      let doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4'
      })
      doc.setFont('helvetica')
      return doc
    },
    startProcess () {
      if (!this.isValid()) { return }

      this.pdf.list = []
      this.csv.list = []
      for (let report in this.selectedReports) {
        if (this.selectedReports[report]) {
          this.reportStatus[report] = 'on-queue'
          this.pdf.list.push(report)
          this.csv.list.push(report)
        }
      }

      this.pdf.loading = true
      this.generateReport()
    },
    generateReport (isCsv = false) {
      if (((!this.pdf.list.length && this.pdfChecked) || !this.pdfChecked) &&
        ((!this.csv.list.length && this.csvChecked) || !this.csvChecked)
      ) {
        this.pdf.loading = false
        return
      }

      if (isCsv) {
        console.log('Processing:', this.csv.list[0])
        this.getDataReports(this.csv.list[0])
      } else {
        console.log('Processing:', this.pdf.list[0])
        this.getDataReports(this.pdf.list[0])
      }
    },
    getDataReports (type) {
      let self = this
      self.reportStatus[type] = 'loading'
      let orientationMax = self.orientation
      self.pdf.maxRecordPerPage = orientationMax.indexOf(type) > -1 ? 25 : 40
      if (type === 'insurance') self.pdf.maxRecordPerPage = 14
      self.$sdk.get(self.apiParams(type).url, self.apiParams(type).data, r => {
        // console.log('---', r)
        // return
        if (r.error) {
          self.reportStatus[type] = 'has-error'
          return
        }

        if (type === 'insuranceMonthly') {
          self.insuranceModal = {
            show: true,
            message: '<b>Insurance Report (Monthly)</b> is being processed... Please check your email.'
          }
          self.pdf.list.shift()
          self.csv.list.shift()
          self.generateReport()
          return
        }

        self.setPdfLabel(type)
        self.pdf.table.data = {}
        self.pdf.table.extra = {}
        self.pdf.table.total = {}
        self.csv.data = []
        self.csv.total = {}

        if (r.list.length > 0) {
          self.buildPdfData(type, r.list)
        } else {
          self.error.show = true
          self.error.empty.push(self.reportLabel(type))
          self.reportStatus[type] = 'has-error'
          self.pdf.list.shift()
          self.csv.list.shift()
          self.generateReport()
        }
      })
    },
    apiParams (type) {
      let url = ''
      let start = moment(this.from)
      let end = moment(this.to)
      let data = {
        token: this.$store.state.session.sessionToken,
        since: start.format('YYYY-MM-DD'),
        until: end.format('YYYY-MM-DD')
      }

      switch (type) {
        case 'payout':
          url = '/reports/payout'
          break
        case 'serviceTypeBrand':
          url = '/reports/services/branded'
          data.filter = this.selectedServiceBrand
          break
        case 'cancellationReport':
          url = '/reports/rsvp/canceled'
          data.cancelSource = this.selectedCancellationType
          if (this.selectedCancellationType === '_admin') {
            data.cancelSource = 'admin'
            data.adminId = this.searchAdminId
          }
          if (this.selectedCancellationType === 'goRes') {
            data.consumerId = 219990
          }
          break
        case 'driverPaymentBonus':
          url = '/reports/rsvp/bonus'
          break
        case 'insurance':
          url = '/reports/insurance'
          data.since = moment.tz(start.format('MM/DD/YYYY 00:00'), 'MM/DD/YYYY HH:mm', 'America/Los_Angeles').unix()
          data.until = moment.tz(end.format('MM/DD/YYYY 00:00'), 'MM/DD/YYYY HH:mm', 'America/Los_Angeles').unix()
          break
        case 'insuranceMonthly':
          url = '/reports/insurance/async'
          data.email = this.insuranceEmail
          data.since = moment.tz(start.format('MM/DD/YYYY 00:00'), 'MM/DD/YYYY HH:mm', 'America/Los_Angeles').unix()
          data.until = moment.tz(end.format('MM/DD/YYYY 00:00'), 'MM/DD/YYYY HH:mm', 'America/Los_Angeles').unix()
          if (this.miles) {
            data.miles = this.miles
          }
          break
        case 'portalFee':
          url = '/reports/portalfee'
          data.brandId = this.selectedBrand
          break
      }
      return { url: url, data: data }
    },
    setPdfLabel (type) {
      let start = moment(this.from)
      let end = moment(this.to)
      let dateRange = start.format('MM/DD/YYYY') + ' - ' + end.format('MM/DD/YYYY')

      this.pdf.code = type
      if (type === 'payout') {
        this.pdf.class = 'report-payout'
        this.pdf.title = 'Opoli Reports - Payout'
        this.pdf.date = 'Driver Payouts and Fees for ' + dateRange
        let riderFeeLabel = this.$settings.get('TravelInsuranceLabel') || 'Rider Fee'
        riderFeeLabel = riderFeeLabel.replace(' ', ' <br/>')
        this.pdf.table.label = [
          'Company', 'Driver <br/>Count', 'RSVP <br/>Count', 
          // 'Total <br/>PAX', 'Avg <br/>PAX', 
          'Fare', riderFeeLabel, 'Airport <br/>Fee', 'Total <br/>Fare', 'Avg <br/>Fare',
          // 'Average <br/>Fare PP', 'Charged',
          'Driver <br/>Payment', 'Payment %', 'Pickup <br/>Fee', 'Fuel <br/>Surcharge',
          'Tips', 'Bonus', 'Cancel <br/>Fee', 'Total <br/>Payout', 'Payout %'
        ]
      }

      if (type === 'serviceTypeBrand') {
        this.pdf.class = 'service-brand'
        this.pdf.title = 'Reservations By Service Type'
        // this.pdf.date = 'By Booking Date (' + dateRange + ')'
        this.pdf.table.label = [
          'Service Type', 'RSVP <br/>Count', 'ASAP', 'Web <br/>Bookings', 'Admin',
          'Consumer <br/>App', 'Go Res', 'Total <br/>PAX', 'Avg <br/>PAX',
          'Total Fare', 'Avg <br/>Fare', 'Avg <br/>Fare PP'
        ]

        switch (this.selectedServiceBrand) {
          case 'bookingTime':
          this.pdf.date = 'By Booking Date (' + dateRange + ')'
          break
          case 'completeTime':
          this.pdf.date = 'By Completion Date (' + dateRange + ')'
          break
          case 'cancelTime':
          this.pdf.date = 'By Cancellation Date (' + dateRange + ')'
          break
          case 'pickupTime':
          this.pdf.date = 'Scheduled Pickup Dates (' + dateRange + ')'
          break
        }
      }

      if (type === 'cancellationReport') {
        this.pdf.class = 'cancellation-rsvp'
        this.pdf.title = 'Cancelled Reservations'
        this.pdf.table.label = [
          'RSVP ID', 'Brand', 'Customer <br/>Name', 'Service', 'Fare', 'Cancelled <br/>Date',
          'Cancelled <br/>By', 'Consumer Notes', 'Reason', 'Comments'
        ]
      }

      if (type === 'driverPaymentBonus') {
        this.pdf.class = 'driver-payment-bonus'
        this.pdf.title = 'Variance Report for Driver Payment'
        this.pdf.table.label = [
          'RSVP ID', 'Brand', 'Service', 'Driver Name', 'Base <br/>Fare', 'Driver <br/>Payment',
          'Variance <br/>Credit', 'Variance <br/>Credit %', 'Total Driver <br/>Payment',
          'Updated <br/>By', 'Updated <br/>On'
        ]
      }

      if (type === 'insurance') {
        this.pdf.class = 'insurance'
        this.pdf.title = 'Insurance Report (Reservations completed by drivers)'
        this.pdf.date = dateRange
        this.pdf.table.label = [
          'RSVP ID',/*'Event ID',*/ 'Driver ID', 'Type', 'Start', 'End',
          'Distance', 'Pickup', 'Dropoff', 'On Duty',
          'OTW', 'On Board', /*'No Show',*/ 'Complete'
        ]
      }

      if (type === 'portalFee') {
        this.pdf.class = 'portal-fee'
        this.pdf.title = 'Portal Fee Report'
        this.pdf.date = dateRange
        this.pdf.table.label = [
          'Brand', 'RSVP <br/>ID', 'Total Fare', 'Portal Fee'
        ]
      }
    },
    buildPdfData (type, pdfData) {
      let self = this
      let groupCount = 0
      let page = 1

      // payout
      let totalDrivers = 0
      let totalRsvp = 0
      let totalPickupFee = 0
      let totalTips = 0
      let totalBonus = 0
      let totalCancelFee = 0
      let totalPax = 0
      let totalAvgPax = 0
      let totalFare = 0
      let totalFareSum = 0
      let totalAvgFare = 0
      let totalTravelInsurance = 0
      let totalAirportFee = 0
      let totalGasFee = 0
      let totalPayment = 0
      let totalBidAmount = 0
      let totalPaymentPercentage = 0
      let totalAvgFarePP = 0
      let totalDriverPayment = 0
      let totalDriverPercentage = 0
      let totalPortalFee = 0

      // serviceTypeBrand
      let totalGo = 0
      let serviceTypeBrand = {
        total: {},
        data: {}
      }

      // driverPaymentBonus
      let totalAvgBonus = 0
      let totalDriverPaymentComputed = 0

      // insurance
      let totalP1 = 0
      let totalP2 = 0
      let totalP3 = 0
      let uniqueRSVP = []

      pdfData.forEach(function (data, index) {
        if (!self.pdf.table.data['page' + page]) {
          self.pdf.table.data['page' + page] = []
        }

        if (type !== 'serviceTypeBrand') {
          self.pdf.table.data['page' + page].push(self.buildTableData(type, data))
        }
        if (self.csvChecked) {
          self.csv.data.push(self.buildCsvData(type, data))
        }
        groupCount++

        if (type === 'payout') {
          totalDrivers += data.drivers
          totalRsvp += data.rsvp
          // totalPax += data.paxSum
          // totalAvgPax += parseFloat(data.paxAvg)
          totalFare += data.bidAmount
          totalTravelInsurance += parseFloat(data.travelInsurance || 0)
          totalAirportFee += parseFloat(data.airportFee || 0)
          totalFareSum += data.fareSum
          totalAvgFare += parseFloat(data.fareAvg)
          totalDriverPayment += parseFloat((data.payment + data.bonus + data.gasFee + data.pickupFee + data.tips) - data.cancelFee)
          totalPayment += data.payment
          totalBidAmount += data.bidAmount
          totalPickupFee += data.pickupFee
          totalGasFee += data.gasFee
          totalTips += data.tips
          totalBonus += data.bonus
          totalCancelFee += data.cancelFee
          totalPaymentPercentage += parseFloat(data.paymentPercent || 0)
          totalDriverPercentage += parseFloat(data.percent)
        }

        if (type === 'serviceTypeBrand') {
          data.services.forEach(function (dat) {
            if (!serviceTypeBrand.data[data.name]) { serviceTypeBrand.data[data.name] = {} }
            let brandCode = ''
            // if (dat.serviceId > 5) {
            //   console.log('data.name', data.name)
            //   brandCode = CONFIG.brands.filter(x => x.name.toLowerCase() === data.name.toLowerCase())[0]
            //   brandCode = '[' + brandCode.code.toUpperCase() + '] '
            // }
            let name = brandCode + dat.name
            serviceTypeBrand.data[data.name][name] = {
              name: name,
              rsvp: self.covertNumber(parseFloat(dat.rsvp)),
              asap: self.covertNumber(parseFloat(dat.asap)),
              web: self.covertNumber(parseFloat(dat.web)),
              admin: self.covertNumber(parseFloat(dat.admin)),
              app: self.covertNumber(parseFloat(dat.app)),
              go: self.covertNumber(parseFloat(dat.go)),
              paxSum: self.covertNumber(parseFloat(dat.paxSum)),
              paxAvg: self.covertNumber(parseFloat(dat.paxAvg).toFixed(2)),
              fareSum: self.covertNumber(parseFloat(dat.fareSum).toFixed(2)),
              fareAvg: self.covertNumber(parseFloat(dat.fareAvg).toFixed(2)),
              farePP: self.covertNumber(parseFloat(dat.farePP).toFixed(2))
            }

            if (!serviceTypeBrand.total[data.name]) {
              serviceTypeBrand.total[data.name] = {
                rsvp: 0, asap: 0, web: 0, admin: 0, app: 0, go: 0,
                paxSum: 0, paxAvg: 0, fareSum: 0, fareAvg: 0, farePP: 0,
              }
            }
            serviceTypeBrand.total[data.name].rsvp += dat.rsvp
            serviceTypeBrand.total[data.name].asap += dat.asap
            serviceTypeBrand.total[data.name].web += dat.web
            serviceTypeBrand.total[data.name].admin += dat.admin
            serviceTypeBrand.total[data.name].app += dat.app
            serviceTypeBrand.total[data.name].go += dat.go
            serviceTypeBrand.total[data.name].paxSum += dat.paxSum
            serviceTypeBrand.total[data.name].paxAvg += parseFloat(dat.paxAvg)
            serviceTypeBrand.total[data.name].fareSum += parseFloat(dat.fareSum)
            serviceTypeBrand.total[data.name].fareAvg += parseFloat(dat.fareAvg)
            serviceTypeBrand.total[data.name].farePP += parseFloat(dat.farePP)
          })

          let compPaxAvg = serviceTypeBrand.total[data.name].paxSum / serviceTypeBrand.total[data.name].rsvp
          let compFareAvg = serviceTypeBrand.total[data.name].fareSum / serviceTypeBrand.total[data.name].rsvp
          let compFarePPAvg = serviceTypeBrand.total[data.name].fareSum / serviceTypeBrand.total[data.name].paxSum

          serviceTypeBrand.total[data.name].paxAvg = self.covertNumber(compPaxAvg.toFixed(2))
          serviceTypeBrand.total[data.name].fareSum = self.covertNumber(parseFloat(serviceTypeBrand.total[data.name].fareSum).toFixed(2))
          serviceTypeBrand.total[data.name].fareAvg = self.covertNumber(compFareAvg.toFixed(2))
          serviceTypeBrand.total[data.name].farePP = self.covertNumber(compFarePPAvg.toFixed(2))
        }

        if (type === 'cancellationReport') {
          totalFare += parseFloat(data.fare)
        }

        if (type === 'driverPaymentBonus') {
          totalRsvp++
          totalFare += parseFloat(data.baseFare)
          totalDriverPayment += parseFloat(data.driverPayment)
          totalBonus += parseFloat(data.bonusAmount)
          // totalAvgBonus += parseFloat(data.bonusAmount / data.baseFare)
          totalDriverPaymentComputed += parseFloat(data.driverPayment + data.bonusAmount)
        }

        if (type === 'insurance' && data.driverId !== 0) {
          if (data[2] == 'P1') totalP1 += data[6]
          if (data[2] == 'P2') totalP2 += data[6]
          if (data[2] == 'P3') totalP3 += data[6]

          if (uniqueRSVP.indexOf(data[5]) == -1) uniqueRSVP.push(data[5])
        }

        if (type === 'portalFee') {
          totalRsvp++
          if (data.overridePrice > 0) totalFare += parseFloat(data.overridePrice)
          else {
            let tmpTotal = parseFloat(data.baseFare) + parseFloat(data.airportFee) + parseFloat(data.gasFee) + parseFloat(data.pickupFee) + parseFloat(data.tipAmount) + parseFloat(data.travelInsurance)
            totalFare += parseFloat(tmpTotal.toFixed(2))
          }
          totalPortalFee += parseFloat(data.portalFee)
        }

        if ((index + 1) % self.pdf.maxRecordPerPage === 0 && pdfData.length !== index + 1) { page++ }
      })

      self.pdf.table.total.record = pdfData.length
      if (type === 'payout') {
        self.pdf.table.total.drivers = self.covertNumber(totalDrivers)
        self.pdf.table.total.rsvp = self.covertNumber(totalRsvp)
        // self.pdf.table.total.charged = self.covertNumber(totalCharged.toFixed(2))
        self.pdf.table.total.pickupFee = self.covertNumber(totalPickupFee.toFixed(2))
        self.pdf.table.total.gasFee = self.covertNumber(totalGasFee.toFixed(2))
        self.pdf.table.total.tips = self.covertNumber(totalTips.toFixed(2))
        self.pdf.table.total.bonus = self.covertNumber(totalBonus.toFixed(2))
        self.pdf.table.total.cancelFee = self.covertNumber(totalCancelFee.toFixed(2))
        // self.pdf.table.total.pax = self.covertNumber(totalPax)
        // self.pdf.table.total.avgPax = self.covertNumber((totalPax / totalRsvp).toFixed(2))
        self.pdf.table.total.fare = self.covertNumber(totalFare.toFixed(2))
        self.pdf.table.total.travelInsurance = self.covertNumber(totalTravelInsurance.toFixed(2))
        self.pdf.table.total.airportFee = self.covertNumber(totalAirportFee.toFixed(2))
        self.pdf.table.total.fareSum = self.covertNumber(totalFareSum.toFixed(2))
        self.pdf.table.total.avgFare = self.covertNumber((totalFareSum / totalRsvp).toFixed(2))
        self.pdf.table.total.payment = self.covertNumber(totalPayment.toFixed(2))
        let paymentPercent = (totalPayment / totalFareSum) * 100
        self.pdf.table.total.paymentPercent = self.covertNumber(paymentPercent.toFixed(2))
        // self.pdf.table.total.avgFarePP = self.covertNumber(totalAvgFarePP.toFixed(2))

        let averagePercentage = totalDriverPercentage / pdfData.length
        self.pdf.table.total.extraFee = self.covertNumber(totalDriverPayment.toFixed(2))
        self.pdf.table.total.percent = self.covertNumber(((totalDriverPayment / totalFareSum) * 100).toFixed(2))
      }
      if (type === 'serviceTypeBrand') {
        self.pdf.table.total = serviceTypeBrand.total

        // let counter = 0
        self.pdf.table.serviceTypeBrandData = {}
        for (let data in serviceTypeBrand.data) {
          if (!self.pdf.table.serviceTypeBrandData['page' + page]) { self.pdf.table.serviceTypeBrandData['page' + page] = {} }
          self.pdf.table.serviceTypeBrandData['page' + page][data] = serviceTypeBrand.data[data]
          // counter++

          // if (counter % 2 === 0) {
          //   counter = 0
          // }
          page++
        }
        page = Object.keys(self.pdf.table.serviceTypeBrandData).length
      }
      if (type === 'cancellationReport') {
        self.pdf.table.total.fare = self.covertNumber(totalFare.toFixed(2))
      }
      if (type === 'driverPaymentBonus') {
        self.pdf.table.total.baseFare = self.covertNumber(totalFare.toFixed(2))
        self.pdf.table.total.driverPayment = self.covertNumber(totalDriverPayment.toFixed(2))
        self.pdf.table.total.bonus = self.covertNumber(totalBonus.toFixed(2))
        totalAvgBonus = (totalBonus / totalFare) * 100
        self.pdf.table.total.avgBonus = self.covertNumber((totalAvgBonus).toFixed(2))
        self.pdf.table.total.totalDriverPayment = self.covertNumber((totalDriverPaymentComputed).toFixed(2))
      }
      if (type === 'insurance') {
        self.pdf.table.total.p1 = self.covertNumber(totalP1.toFixed(2))
        self.pdf.table.total.p2 = self.covertNumber(totalP2.toFixed(2))
        self.pdf.table.total.p3 = self.covertNumber(totalP3.toFixed(2))
        self.pdf.table.total.overAll = self.covertNumber((totalP1 + totalP2 + totalP3).toFixed(2))
        self.pdf.table.total.rsvp = uniqueRSVP.length
      }
      if (type === 'portalFee') {
        self.pdf.table.total.rsvp = self.covertNumber(totalRsvp)
        self.pdf.table.total.fare = self.covertNumber(totalFare.toFixed(2))
        self.pdf.table.total.portalFee = self.covertNumber(totalPortalFee.toFixed(2))
      }

      if (self.pdfChecked) {
        self.processDownloads('pdf', {type: type, page: page})
      } else {
        self.processDownloads('csv', {type: type})
      }
    },
    buildTableData (type, data) {
      let self = this
      if (type === 'payout') {
        let driverPayment = (data.payment + data.bonus + data.pickupFee + data.tips) - data.cancelFee
        driverPayment += data.gasFee
        return {
          company: data.name,
          driverCount: self.covertNumber(data.drivers),
          rsvpCount: self.covertNumber(data.rsvp),
          // totalPax: self.covertNumber(data.paxSum),
          // avgPax: self.covertNumber(parseFloat(data.paxAvg).toFixed(2)),
          fare: self.covertNumber(parseFloat(data.bidAmount).toFixed(2)),
          travelInsurance: self.covertNumber(parseFloat(data.travelInsurance || 0).toFixed(2)),
          airportFee: self.covertNumber(parseFloat(data.airportFee || 0).toFixed(2)),
          totalFare: self.covertNumber(parseFloat(data.fareSum).toFixed(2)),
          avgFare: self.covertNumber(parseFloat(data.fareAvg).toFixed(2)),
          // avgFarePP: self.covertNumber(parseFloat(data.farePP).toFixed(2)),
          // charged: self.covertNumber(parseFloat(data.charged).toFixed(2)),
          payment: self.covertNumber(parseFloat(data.payment).toFixed(2)),
          paymentPercent: self.covertNumber(parseFloat(data.paymentPercent || 0).toFixed(2)),
          pickupFee: self.covertNumber(parseFloat(data.pickupFee).toFixed(2)),
          gasFee: self.covertNumber(parseFloat(data.gasFee).toFixed(2)),
          tips: self.covertNumber(parseFloat(data.tips).toFixed(2)),
          bonus: self.covertNumber(parseFloat(data.bonus).toFixed(2)),
          cancelFee: self.covertNumber(parseFloat(data.cancelFee).toFixed(2)),
          driverPayment: self.covertNumber(parseFloat(driverPayment).toFixed(2)),
          driverPercent: self.covertNumber(parseFloat(data.percent || 0).toFixed(2))
        }
      }
      if (type === 'cancellationReport') {
        let index = CONFIG.brands.findIndex(x => x.id === data.brandId)
        let cancelBy = 'SYSTEM'
        let comments = data.comments
        if (data.cancelAdminId) {
          cancelBy = self.admins.filter(x => x.id === data.cancelAdminId)[0].name
        } else if (data.cancelFrom) {
          if (data.cancelFrom === 'consumer') {
            cancelBy = 'SELF'
          } else if (data.cancelFrom === 'provider') {
            cancelBy = 'DRIVER'
          }
        }
        if (data.isAsap === 1 && (data.cancelAdminId || data.cancelFrom === 'provider')) {
          comments = 'asap release'
        }
        if (self.selectedCancellationType === 'consumer') {
          cancelBy = 'SELF'
        }

        if (comments) {
          comments = comments.replace(/<[^>]*>/g, '')
        }

        return {
          rsvpId: data.ID,
          brand: index === -1 ? 'ERROR' : CONFIG.brands[index].code.toUpperCase(),
          customerName: data.name,
          service: data.serviceTypeLabel,
          fare: self.covertNumber(parseFloat(data.fare).toFixed(2)),
          cancelledDate: moment(data.cancelTime, 'YYYY-MM-DD@HH:mm').format('MM/DD/YYYY ' + featureToggle.timeFormat(self, false)),
          cancelledBy: cancelBy,
          consumerNotes: data.consNotes,
          reason: data.paxReason !== null ? data.paxReason : '',
          comments: comments
        }
      }
      if (type === 'driverPaymentBonus') {
        let index = CONFIG.brands.findIndex(x => x.id === data.brandId)
        let updatedBy = 'SYSTEM'
        if (data.bonusAdmin) {
          updatedBy = self.admins.filter(x => x.id === data.bonusAdmin)[0].name
        }

        return {
          rsvpId: data.ID,
          brand: index === -1 ? 'ERROR' : CONFIG.brands[index].code.toUpperCase(),
          service: data.serviceTypeLabel,
          driverName: data.driverName,
          baseFare: self.covertNumber(parseFloat(data.baseFare).toFixed(2)),
          driverPayment: self.covertNumber(parseFloat(data.driverPayment).toFixed(2)),
          bonus: self.covertNumber(parseFloat(data.bonusAmount).toFixed(2)),
          avgBonus: self.covertNumber(parseFloat((data.bonusAmount / data.baseFare) * 100).toFixed(2)),
          totalDriverPayment: self.covertNumber(parseFloat(data.bonusAmount + data.driverPayment).toFixed(2)),
          updatedBy: updatedBy,
          updatedOn: moment(data.bonusTime, 'YYYY-MM-DD@HH:mm').format('MM/DD/YYYY ' + featureToggle.timeFormat(self, false))
        }
      }
      if (type === 'insurance') {
        return {
          rsvpId: data[5],
          // eventId: data[0],
          driverId: data[1],
          type: data[2],
          start: moment.tz(data[3], 'YYYY-MM-DD@HH:mm', 'GMT').tz('America/Los_Angeles').format('MM/DD/YYYY HH:mm'),
          end: moment.tz(data[4], 'YYYY-MM-DD@HH:mm', 'GMT').tz('America/Los_Angeles').format('MM/DD/YYYY HH:mm'),
          distance: data[6],
          pickup: data[7],
          dropoff: data[8],
          onDuty: data[9] ? moment.tz(data[9], 'YYYY-MM-DD@HH:mm', 'GMT').tz('America/Los_Angeles').format('MM/DD/YYYY HH:mm') : '',
          otw: data[10] ? moment.tz(data[10], 'YYYY-MM-DD@HH:mm', 'GMT').tz('America/Los_Angeles').format('MM/DD/YYYY HH:mm') : '',
          onBoard: data[11] ? moment.tz(data[11], 'YYYY-MM-DD@HH:mm', 'GMT').tz('America/Los_Angeles').format('MM/DD/YYYY HH:mm') : '',
          // noShow: data[12] ? moment(data[12], 'YYYY-MM-DD@HH:mm').format('MM/DD/YYYY ' + featureToggle.timeFormat(self, false)) : '',
          complete: data[13] ? moment.tz(data[13], 'YYYY-MM-DD@HH:mm', 'GMT').tz('America/Los_Angeles').format('MM/DD/YYYY HH:mm') : '',
          // offDuty: data[14] ? moment(data[14], 'YYYY-MM-DD@HH:mm').format('MM/DD/YYYY ' + featureToggle.timeFormat(self, false)) : ''
        }
      }
      if (type === 'portalFee') {
        let baseFare = parseFloat(data.baseFare) + parseFloat(data.airportFee) + parseFloat(data.gasFee) + parseFloat(data.pickupFee) + parseFloat(data.tipAmount) + parseFloat(data.travelInsurance)
        baseFare = parseFloat(baseFare.toFixed(2))
        if (data.overridePrice > 0) baseFare = data.overridePrice
        return {
          brand: data.brand,
          rsvpID: data.ID,
          baseFare: baseFare,
          portalFee: data.portalFee
        }
      }
    },
    fileName (type, format) {
      let timestamp = moment(this.from).format('YYYYMMDD') + '_' + moment(this.to).format('YYYYMMDD')
      switch (type) {
        case 'payout': return 'reportspayout_' + timestamp + '.' + format
        case 'serviceTypeBrand': return 'servicetypebrand_' + timestamp + '.' + format
        case 'cancellationReport': return 'cancelledreport_' + timestamp + '.' + format
        case 'driverPaymentBonus': return 'variance_driverpaymentbonus_' + timestamp + '.' + format
        case 'insurance': return 'insurancereport_' + timestamp + '.' + format
        case 'portalFee': return 'portalfee_' + timestamp + '.' + format
      }
    },
    currentPage (page) { return page.substring(4) },
    processPdf (type, page) {
      let self = this
      let start = moment(self.from)
      let end = moment(self.to)
      let pageAsyncs  = []
      let landscapeReports = self.orientation
      let orientation = landscapeReports.indexOf(type) > -1 ? 'l' : 'p'
      let pdf = new jsPDF({ orientation: orientation, unit: 'mm', format: 'a4' })

      window.scrollTo(500, 0)
      document.body.style.overflow = 'hidden'

      setTimeout(() => {
        // for (let i = 0; i < page; i++) {
        //   console.log('i -', i + 1)
        //   pageAsyncs.push(html2canvas(document.getElementsByClassName('page' + (i + 1))[0], {
        //     scale: "1"
        //   }).then(canvas => {
        //     let imgData = canvas.toDataURL('image/jpeg', 1.0)
        //     pdf.addImage(imgData, 'JPEG', 0, 0)
        //     if (i + 1 <= page) {
        //       console.log('add page', i + 1)
        //       pdf.addPage()
        //     }
        //   }).catch(e => {
        //     self.reportStatus[type] = 'has-error'
        //     self.pdf.list.shift()
        //     document.body.style.overflow = 'auto'
        //     self.processDownloads('csv', {type: type})
        //     console.warn(e)
        //   }))
        // }

        var i = 0
        var buildPdfPages = (i) => {
          if (i + 1 > page) {
            downloadPdfPages()
            return
          }
          pageAsyncs.push(html2canvas(document.getElementsByClassName('page' + (i + 1))[0], {
            scale: "1"
          }).then(canvas => {
            let imgData = canvas.toDataURL('image/jpeg', 1.0)
            pdf.addImage(imgData, 'JPEG', 0, 0)
            if (i + 1 <= page) {
              pdf.addPage()
              buildPdfPages(i + 1)
            }
          }).catch(e => {
            self.reportStatus[type] = 'has-error'
            self.pdf.list.shift()
            document.body.style.overflow = 'auto'
            self.processDownloads('csv', {type: type})
            console.warn(e)
          }))
        }
        buildPdfPages(0)

        var downloadPdfPages = () => {
          pdf.save(self.fileName(type, 'pdf'))
          self.reportStatus[type] = 'success'
          self.pdf.list.shift()
          document.body.style.overflow = 'auto'
          self.processDownloads('csv', {type: type})
        }

        // Promise.all(pageAsyncs).then(() => {
        //   let timestamp = start.format('YYYYMMDD') + '_' + end.format('YYYYMMDD')
        //   pdf.save(self.fileName(type, 'pdf'))
        //   self.reportStatus[type] = 'success'
        //   self.pdf.list.shift()
        //   document.body.style.overflow = 'auto'
        //   self.processDownloads('csv', {type: type})
        // })
      }, 500)
    },
    covertNumber (value) { return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") },
    resetSelectedStatus () {
      this.selectedReports = {
        payout: false,
        serviceTypeBrand: false,
        cancellationReport: false,
        driverPaymentBonus: false,
        insurance: false,
        insuranceMonthly: false,
        portalFee: false
      }
    },
    resetReportStatus () {
      this.error.show = false
      this.error.empty = []
      this.reportStatus = {
        payout: '',
        serviceTypeBrand: '',
        cancellationReport: '',
        driverPaymentBonus: '',
        portalFee: ''
      }
    },
    reportLabel (type) {
      switch (type) {
        case 'payout': return 'Payout Report'
        case 'serviceTypeBrand': return 'Reservation by Service Type (Per Brand)'
        case 'cancellationReport': return 'Cancellation Report'
        case 'driverPaymentBonus': return 'Variance Report for Driver Payment'
        case 'insurance': return 'Insurance Report (Daily)'
        case 'insuranceMonthly': return 'Insurance Report (Monthly)'
        case 'portalFee': return 'Portal Fee'
      }
    },
    buildCsvData (type, data) {
      let column = []
      this.pdf.table.label.forEach(function(i) {
        column.push(i.replace('<br/>', ''))
      })
      this.csv.label = column

      let csvData = {}
      let pdfData = this.buildTableData(type, data)

      let i = 0
      for (var key in pdfData) {
        csvData[column[i]] = pdfData[key]
        i++
      }
      return csvData
    },
    processDownloads (action, data) {
      if (action === 'pdf' && this.pdfChecked) {
        this.processPdf(data.type, data.page)
      } else if (action === 'csv' && this.csvChecked) {
        this.processCsv(data.type)
      } else {
        this.generateReport()
      }
    },
    processCsv (type) {
      const exporter = CsvExport.create({
        filename: this.fileName(type, 'csv'),
        columns: this.csv.label
      })

      // add total
      this.csv.data.push(this.buildCsvTotal(type))

      exporter.downloadCsv(this.csv.data)
      this.reportStatus[type] = 'success'
      this.csv.list.shift()

      this.generateReport(true)
    },
    buildCsvTotal (type) {
      let csvData = {}
      let totalData = this.pdf.table.total

      if (type === 'payout') {
        csvData[this.csv.label[0]] = 'Total (' + totalData.record + ')'
        csvData[this.csv.label[1]] = totalData.drivers
        csvData[this.csv.label[2]] = totalData.rsvp
        // csvData[this.csv.label[3]] = totalData.pax
        // csvData[this.csv.label[4]] = totalData.avgPax
        csvData[this.csv.label[3]] = totalData.fare
        csvData[this.csv.label[4]] = totalData.travelInsurance
        csvData[this.csv.label[5]] = totalData.airportFee
        csvData[this.csv.label[6]] = totalData.fareSum
        csvData[this.csv.label[7]] = totalData.avgFare
        csvData[this.csv.label[8]] = totalData.payment
        csvData[this.csv.label[9]] = totalData.paymentPercent
        csvData[this.csv.label[10]] = totalData.pickupFee
        csvData[this.csv.label[11]] = totalData.gasFee
        csvData[this.csv.label[12]] = totalData.tips
        csvData[this.csv.label[13]] = totalData.bonus
        csvData[this.csv.label[14]] = totalData.cancelFee
        csvData[this.csv.label[15]] = totalData.extraFee
        csvData[this.csv.label[16]] = totalData.percent
      }

      if (type === 'cancellationReport') {
        csvData[this.csv.label[0]] = 'Total (' + totalData.record + ')'
        csvData[this.csv.label[1]] = ''
        csvData[this.csv.label[2]] = ''
        csvData[this.csv.label[3]] = ''
        csvData[this.csv.label[4]] = totalData.fare
        csvData[this.csv.label[5]] = ''
        csvData[this.csv.label[6]] = ''
        csvData[this.csv.label[7]] = ''
        csvData[this.csv.label[8]] = ''
        csvData[this.csv.label[9]] = ''
      }

      if (type === 'driverPaymentBonus') {
        csvData[this.csv.label[0]] = 'Total (' + totalData.record + ')'
        csvData[this.csv.label[1]] = ''
        csvData[this.csv.label[2]] = ''
        csvData[this.csv.label[3]] = ''
        csvData[this.csv.label[4]] = totalData.baseFare
        csvData[this.csv.label[5]] = totalData.driverPayment
        csvData[this.csv.label[6]] = totalData.bonus
        csvData[this.csv.label[7]] = totalData.avgBonus
        csvData[this.csv.label[8]] = totalData.totalDriverPayment
        csvData[this.csv.label[9]] = ''
        csvData[this.csv.label[10]] = ''
      }

      if (type === 'insurance') {
        csvData[this.csv.label[0]] = 'Total'
        csvData[this.csv.label[1]] = `RSVP: ${totalData.rsvp}`
        csvData[this.csv.label[2]] = `P1: ${totalData.p1}`
        csvData[this.csv.label[3]] = `P2: ${totalData.p2}`
        csvData[this.csv.label[4]] = `P3: ${totalData.p3}`
        csvData[this.csv.label[5]] = `Grand Total: ${totalData.overAll}`
      }

      return csvData
    },
    formatCheck () {
      this.error.show = false
      this.formatError = false
    },
    selectAdmin (admin) {
      this.searchAdmin = admin.name
      this.searchAdminId = admin.id
      this.showSearchAdminList = false
    },
    adminSearchFocus () {
      this.showSearchAdminList = true
      if (this.searchAdminDelay) {
        clearTimeout(this.searchAdminDelay)
      }
    },
    adminSearchBlur () {
      if (this.searchAdminDelay) {
        clearTimeout(this.searchAdminDelay)
      }
      this.searchAdminDelay = setTimeout(() => {
        this.showSearchAdminList = false
      }, 500)
    },
    isNumber (evt) {
      let charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
        evt.preventDefault()
        return
      }
      return true;
    },
  },
  watch: {
    show (value) {
      if (!value) { return }

      this.from = null
      this.to = null
      this.error.show = false
      this.error.empty = []

      this.resetSelectedStatus()
      this.resetReportStatus()
    },
    from (value) { this.resetReportStatus() },
    to (value) { this.resetReportStatus() },
    'selectedReports.payout' (value) { this.resetReportStatus() },
    'selectedReports.serviceTypeBrand' (value) { this.resetReportStatus() },
    'selectedReports.cancellationReport' (value) { this.resetReportStatus() },
    'selectedReports.driverPaymentBonus' (value) { this.resetReportStatus() },
    'selectedReports.insurance' (value) { this.resetReportStatus() },
    'selectedReports.portalFee' (value) { this.resetReportStatus() }
  }
}
</script>

<style lang="scss" scoped>
.rsvp-report-box {
  transform-origin: top center;
  position: relative;
  width: 100%;
}
.rsvp-report {
  border-radius: 3px;
  padding: 0 20px 20px;

  & .title {
    text-align: center;
    font-weight: 700;
    margin-bottom: 20px;
    font-size: 18px;
  }

  & .date-input {
    margin-bottom: 10px;

    & label {
      font-size: 13px;
      color: #999;
      display: block;
    }
    select{
      padding: 10px;
      height: 46px;
      outline: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      border-color: #cacaca;
      font-size: 14px;
      color: #333;
      background-image: url('/assets/img/dropdown-arrow.png');
      background-repeat: no-repeat;
      background-position: right 50%;

      &[disabled]{ background-color: #ebebe4; }
    }
  }

  .reports{
    max-height: 350px;
    margin-bottom: 10px;
    overflow: auto;
    clear: both;
    background: #fafafa;
    border: 1px solid #ddd;
    position: relative;

    .report{
      position: relative;
      z-index: 1;

      &:hover{ background:#f1f1f1; }
      &.processing{
        background: rgba(0,0,0, .7);
        input{ opacity: .5; }
      }
      select{
        margin-left: 28px;
        margin-bottom: 10px;
        width: calc(100% - 43px);
        border-color: #999;
        font-size: 13px;
        color: #333;
        padding: 3px 5px;
        transition: all .2s ease;

        &.disabled{
          background: #fafafa;
          border-color: #ccc;
        }
        option:disabled{ background: #eaeaea; }
      }
      &.on-queue, &.loading, &.has-error, &.success{
        z-index: 3;
        box-shadow: 0 0 2px rgba(0,0,0, .4);
      }
      &.on-queue{ background: #fff; }
      &.loading{
        color: #775913;
        background: #f1ce7e;
      }
      &.success{
        color: #0f693a;
        background: #a9f7ce;
      }
      &.has-error{
        color: #b93030;
        background: #fbb0b0;
      }
    }
  }
}

.filter{
  list-style: none;
  margin: 0 0 5px;
  padding: 0;
  font-size: 13px;
  display: flex;

  li{
    margin: 2px 0;
    width: 100%;
  }
  label{ cursor: pointer;}
  input{
    display: inline-block;
    margin: 0 3px 0 0;
    width: auto;
    vertical-align: middle;
  }
}

.download-type{
  float: right;
  width: 150px;
  text-align: right;
  padding-bottom: 5px;

  label{
    cursor: pointer;
    display: inline-block;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    padding: 2px 7px;
    border:1px solid #ddd;

    &.pdf{ color: #c54d4d; }
    &.csv{ color: #7ad275; }
    &.has-error{ border: 1px solid #d00; }
    input{
      display: inline;
      margin-top: 0;
      width: auto;
      vertical-align: -1px;
    }
  }
}

.list-box{
  overflow: hidden;
  position: relative;
  padding-bottom: 20px;
}

.error {
  background-color: #F25E0D;
  font-size: 14px;
  text-align: center;
  color: #fff;
  border-radius: 3px;
  padding: 5px;
  margin-bottom: 15px;
  transform-origin: top center;

  ul{
    list-style: none;
    margin: 0;
    padding: 10px;
    text-align: left;

    li:first-child{
      font-weight: 600;
      font-size: 14px;
      line-height: 1;
      margin-bottom: 2px;
    }
    li{
      font-size: 13px;
    }
  }
}

.form-group {
  display: block;

  & .instruction {
    margin-bottom: 5px;
    font-size: 14px;
    font-weight: 600;
    color: #777;
  }
}

.checkbox {
  display: block;
  font-size: 13px;
  cursor: pointer;
  padding: 5px 10px;
  & > * {
    display: inline-block;
    vertical-align: middle;
    margin: 0;
    width: auto;
  }
  input { margin-right: 3px; }
}
.note{
  font-size: 11px;
  letter-spacing: .6px;
  padding-left: 30px;
  display: block;
  line-height: 1;
  margin-bottom: 4px;
  color: #777;
}

.padding-R { padding-right: 7px; }
.padding-L { padding-left: 7px; }

#pdf-view{
  position: fixed;
  top: 100%;
  left: 0;
  width: 793px;
  height: 1122px;
  background: #fff;
  z-index: -1;

  .page{
    padding: 30px;
    position: relative;
    width: 100%;
    height: 100%;

    .page-count{
      position: absolute;
      bottom: 10px;
      right: 30px;
      color: #aaa;
      font-size: 12px;
      text-align: right;
      font-style: italic;
    }
  }
  .title{
    text-align: center;
    font-weight: 600;
    font-size: 21px;
  }
  .date-range{
    text-align: center;
    font-style: italic;
    font-size: 18px;
    margin-bottom: 20px;
    color: #555;
  }

  table{
    width: 100%;
    font-size: 12px;
    border-collapse: collapse;

    thead{
      font-weight: 600;
      tr:last-child th{ border-bottom: 1px solid #333; }
    }
    tbody{
      tr{
        &.subtotal, &.total{ font-weight: 700; }
        &.total{
          color: #000;
          font-size: 12px;
        }
      }
      td{ padding: 2px 3px; }
    }
  }
}

.report-payout{
  width: 1122px!important;
  height: 793px!important;
  table{
    tbody tr.total{
      border-top: 1px solid #333;
      font-weight: 600;
      font-size:11px!important
    }
    .driverCount, .rsvpCount, .totalPax, .avgPax{ text-align: center; }
    .gasFee, .fare, .totalFare, .avgFare, .travelInsurance, .airportFee, .extraFee, .charged, .payment, .pickupFee,
    .tips, .bonus, .cancelFee, .driverPayment, .paymentPercent, .driverPercent{ text-align: right; }
    .driverPayment{ font-weight: 600; }
    .gasFee, .fare, .travelInsurance, .airportFee, .payment, .pickupFee, .tips, .bonus, .cancelFee{ color: #777; }
    .company{
      max-width: 130px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.service-brand{
  width: 1122px!important;
  height: 793px!important;

  .brand-box{ margin-bottom: 50px; }
  table{
    tbody tr.total{
      border-top: 1px solid #333;
      font-weight: 600;
    }
    .rsvp, .asap, .web, .admin, .app, .go, .paxSum, .paxAvg{ text-align: center; }
    .fareSum, .fareAvg, .farePP{ text-align: right; }
    /*.fare, .airportFee, .payment, .pickupFee, .tips, .bonus, .cancelFee{ color: #777; }*/
  }
}

.driver-payment-bonus{
  width: 1122px!important;
  height: 793px!important;

  table{
    .rsvpId, .brand, .updatedOn, .baseFare, .driverPayment, .bonus, .avgBonus, .totalDriverPayment{ text-align: center; }
    // .baseFare, .driverPayment, .bonus, .avgBonus, .totalDriverPayment{ color: #777; }
    .driverName, .updatedBy{
      width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

#pdf-view.insurance{
  width: 1122px!important;
  height: 793px!important;

  table tbody td{ text-align: center; }
  .total-p{
    text-align: left;
    span{
      display: inline-block;
      margin: 0 10px;
    }
  }
}
#pdf-view.insurance{
  table{
    tbody tr.total{ border-top: 1px solid #333; }
    .date, .rsvpCount, .asap, .web, .admin, .app, .insurance{ text-align: center; }
    .totalFare, .avgFare, .extraFee, .driverPayment, .driverPercent{ text-align: right; }
    .driver, .service, .promo{
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .geofences{
      max-width: 110px;
      font-size: 11px;
    }
    .insurance{
      max-width: 70px;
    }
  }
}

.insurance-email{
  padding: 3px 10px;
  margin: 0 10px 10px 25px;
  width: calc(100% - 35px);
  font-size: 13px;

  &::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: #777;
    opacity: 1; /* Firefox */
  }
}

.portal-fee{
  thead th:first-child{
    text-align: left;
  }
  tbody tr.total{ border-top: 1px solid #333; }
  .rsvpID, .baseFare, .portalFee{ text-align: center; }
}

.admin-box {
  position: relative;
  margin: 0 15px 15px 28px;

  input{
    padding: 5px 8px;
    margin: 0 0 5px;
    font-size: 14px;
  }
  ul{
    padding: 0;
    margin: 0;
    list-style: none;
    background: #fafafa;
    border: 1px solid #ccc;
    border-radius: 2px;
    height: 100px;
    overflow: auto;
    li{
      display: block;
      line-height: 1;
      padding: 5px 8px;
      font-size: 14px;
      color: #333;

      &:hover{
        background: #f3f3f3;
      }
    }
  }
}

.modal{
  position: fixed;
  z-index: 12;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: none;
  flex-direction: column;
  justify-content: center;
  resize: vertical;

  .bg-overlay{
    position: absolute;
    z-index: 1;
    background: rgba(0, 0, 0, 0.5);
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }
  .modal-content{
    position: relative;
    z-index: 2;
    background: #fff;
    width: 400px;
    min-height: 150px;
    border-radius: 6px;
    padding: 20px;
    margin: auto;
    box-shadow: 1px 1px 5px 1px rgba(0,0,0, .3);
    display: none;
    text-align: center;
  }
  &.show{
    display: flex;

    .modal-content{
      display: block;
      -webkit-animation: slide-down .15s ease-out;
      -moz-animation: slide-down .15s ease-out;
    }
  }
}

@-webkit-keyframes slide-down {
  0% { opacity: 0; -webkit-transform: translateY(100%); }
  100% { opacity: 1; -webkit-transform: translateY(0); }
}
@-moz-keyframes slide-down {
  0% { opacity: 0; -moz-transform: translateY(100%); }
  100% { opacity: 1; -moz-transform: translateY(0); }
}

.slide-down-enter-active, .slide-down-leave-active { transition: all .3s ease; }
.slide-down-enter, .slide-down-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

@media only screen and (max-width: 430px) {
  .end-date {
    /deep/ .vdp-datepicker__calendar {
      right: 1px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Reports/SpecializeReport.vue